import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [state, setState] = useState("form"); // form | done | mismatch | error:<msg>
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setState("mismatch");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setState("done");
    } catch (err) {
      setState("error:" + errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-reset-page" className="hero-grid flex min-h-[70vh] items-center justify-center px-4 py-16">
      <div className="card-tactical glow-orange w-full max-w-md p-8">
        {state === "done" ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
            <h1 className="mt-4 text-2xl font-extrabold uppercase" data-testid="reset-success-title">Mot de passe modifié !</h1>
            <p className="mt-2 text-sm text-slate-400">Vous pouvez vous connecter avec votre nouveau mot de passe.</p>
            <Link to="/admin/login" data-testid="reset-login-link"
              className="mt-6 inline-block rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
              Se connecter
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center">
              <KeyRound className="mx-auto h-12 w-12 text-orange-500" />
              <h1 className="mt-4 text-2xl font-extrabold uppercase">Nouveau mot de passe</h1>
              <p className="mt-2 text-sm text-slate-400">Choisissez un nouveau mot de passe pour votre compte admin.</p>
            </div>
            {!token && (
              <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400" data-testid="reset-no-token">
                Lien incomplet — utilisez le lien reçu par email.
              </p>
            )}
            {state === "mismatch" && (
              <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400" data-testid="reset-mismatch">
                Les deux mots de passe ne correspondent pas.
              </p>
            )}
            {state.startsWith("error:") && (
              <p className="mt-4 flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400" data-testid="reset-error">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {state.slice(6)}
              </p>
            )}
            <form onSubmit={submit} className="mt-6 grid gap-4">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                Nouveau mot de passe (8 caractères minimum)
                <input type="password" value={form.password} data-testid="reset-password"
                  onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls + " mt-1"} required minLength={8} />
              </label>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                Confirmer le mot de passe
                <input type="password" value={form.confirm} data-testid="reset-confirm"
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls + " mt-1"} required minLength={8} />
              </label>
              <button type="submit" disabled={loading || !token} data-testid="reset-submit"
                className="rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98] disabled:opacity-50">
                {loading ? "Modification..." : "Valider le nouveau mot de passe"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
